import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {ShieldCheckIcon, UserIcon} from '@heroicons/react/24/outline';
import {AccessCard, Badge, Button, SegmentedControl, Spinner, Switch, TextInput} from '../Components';
import {getCallerAccess} from '../Views/Settings/Components/memberAccess';
import {createMcpToken, IMcpToken, TMcpScope} from '../react-sdk/queries';
import {MCP_SCOPES} from '../Views/Settings/mcpScopes';
import {Dialog} from './Dialog';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  expires: z.string(),
  scopes: z.record(z.string(), z.boolean()),
  global_admin: z.boolean(),
});

type SchemaType = z.infer<typeof schema>;

const EXPIRY_OPTIONS = [
  {key: 'never', label: 'Never'},
  {key: '30', label: '30 days'},
  {key: '90', label: '90 days'},
  {key: '365', label: '1 year'},
];

interface CreateMcpTokenDialogProps {
  onClose: () => void;
  onCreated: (token: IMcpToken) => void;
}

export const CreateMcpTokenDialog: FC<CreateMcpTokenDialogProps> = ({onClose, onCreated}) => {
  const [submitting, setSubmitting] = useState(false);
  const {isAdmin} = getCallerAccess();
  const scopeOptions = MCP_SCOPES.filter((scope) => isAdmin || !scope.adminOnly);
  const optional = scopeOptions.filter((scope) => scope.value !== 'read');

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      expires: 'never',
      scopes: {'templates:write': true, 'envelopes:write': true},
      global_admin: false,
    },
  });

  const scopes = useWatch({control: form.control, name: 'scopes'});
  const expires = useWatch({control: form.control, name: 'expires'});
  const globalAdmin = useWatch({control: form.control, name: 'global_admin'});
  const selectedCount = 1 + optional.filter((scope) => scopes[scope.value]).length;

  const setScopes = (changed: TMcpScope[], on: boolean) => {
    const next = {...form.getValues('scopes')};
    changed.forEach((scope) => (next[scope] = on));
    form.setValue('scopes', next, {shouldDirty: true});
  };

  const onSubmit = (data: SchemaType) => {
    // Disabled Buttons can still submit forms
    if (submitting) {
      return;
    }

    setSubmitting(true);
    const granted = optional.map((scope) => scope.value).filter((scope) => data.scopes[scope]);
    const days = data.expires !== 'never' ? Number(data.expires) : null;

    createMcpToken({name: data.name, scopes: ['read', ...granted], global_admin: isAdmin && data.global_admin, expires_in_days: days})
      .then((token) => {
        setSubmitting(false);
        onCreated(token);
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error creating MCP token: ${e.message}`);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="text-lg font-medium text-[#33364b]">Create an MCP token</div>
      <div className="mt-0.5 mb-4 text-[13px] text-gray-500">Connect an AI assistant to your Verdocs account.</div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <TextInput
              label="Name"
              placeholder="Claude Desktop, laptop"
              {...form.register('name')}
              required
              hint="Which assistant and machine this token is for."
            />

            {isAdmin && (
              <div className="mb-4">
                <div className="mb-1.5 text-[13px] font-medium text-[#33364b]">Access</div>
                <div className="grid grid-cols-2 gap-2">
                  <AccessCard
                    selected={!globalAdmin}
                    onSelect={() => form.setValue('global_admin', false, {shouldDirty: true})}
                    icon={<UserIcon />}
                    tone="personal"
                    title="Personal"
                    description="Your own access"
                  />
                  <AccessCard
                    selected={globalAdmin}
                    onSelect={() => form.setValue('global_admin', true, {shouldDirty: true})}
                    icon={<ShieldCheckIcon />}
                    tone="admin"
                    title="Global admin"
                    description="The whole organization"
                  />
                </div>
                {globalAdmin && (
                  <div className="mt-2 rounded-md border border-[#f0d9b5] bg-[#fdf8ef] px-3 py-2 text-[12px] leading-normal text-[#95591a]">
                    A global admin token can read and change every template and envelope in the organization, including ones never shared
                    with you.
                  </div>
                )}
              </div>
            )}

            <div className="mb-1.5 text-[13px] font-medium text-[#33364b]">Expires</div>
            <div className="mb-4">
              <SegmentedControl
                options={EXPIRY_OPTIONS}
                value={expires}
                onChange={(key) => form.setValue('expires', key, {shouldDirty: true})}
              />
            </div>

            <div className="mb-1.5 flex items-baseline justify-between">
              <div className="text-[13px] font-medium text-[#33364b]">Scopes</div>
              <div className="text-[12px] text-gray-500">
                {selectedCount} of {scopeOptions.length} selected ·{' '}
                <button
                  type="button"
                  className="text-[#654dcb] hover:underline"
                  onClick={() =>
                    setScopes(
                      optional.map((s) => s.value),
                      true,
                    )
                  }
                >
                  All
                </button>{' '}
                ·{' '}
                <button
                  type="button"
                  className="text-[#654dcb] hover:underline"
                  onClick={() =>
                    setScopes(
                      optional.map((s) => s.value),
                      false,
                    )
                  }
                >
                  None
                </button>
              </div>
            </div>
            <div className="rounded-lg border border-[#ebecf0] px-3">
              {scopeOptions.map((scope) => (
                <label
                  key={scope.value}
                  className={`flex items-center justify-between gap-4 border-b border-[#f2f3f5] py-2 last:border-0 ${scope.value === 'read' ? '' : 'cursor-pointer'}`}
                >
                  <span>
                    <span className="flex items-center gap-1.5 text-[13px] font-medium leading-5 text-[#33364b]">
                      {scope.label}
                      {scope.value === 'destructive' && (
                        <span className="leading-4">
                          <Badge tone="warn">Destructive</Badge>
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-[12px] leading-normal text-gray-500">{scope.description}</span>
                  </span>
                  {scope.value === 'read' ? (
                    <span className="whitespace-nowrap text-[12px] text-gray-400">Always on</span>
                  ) : (
                    <Switch
                      checked={!!scopes[scope.value]}
                      onCheckedChange={(on) => setScopes([scope.value], on)}
                      className="data-[state=checked]:bg-[#654dcb] data-[state=unchecked]:bg-gray-200"
                    />
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-row items-center justify-end gap-2 border-t border-[#f2f3f5] pt-4">
            {submitting && <Spinner mode="dark" />}
            <Button type="button" size="small" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
            <Button size="small" label="Create token" type="submit" disabled={submitting} />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
